import { apiAgentChat } from '@modules/ai-framework/api/apiAgentChat'
import { apiAgentConversations } from '@modules/ai-framework/api/apiAgentConversations'
import { CHAT } from '@modules/ai-framework/constants/copy'

// Actions do slice `chat` (spec-003 G6). Compostas no store/index.js (namespace `aiFramework`).
// ★ Toda action lê `r.status` do BODY (o backend responde sempre HTTP 200 — Constituição II).

const STATUS_OK = 200
const STATUS_FORBIDDEN = 403
// Status sintético local para o erro de stream inline (não vem do backend) — o
// `system-message-inline` só precisa de um Number para escolher a cor neutra.
const STREAM_ERROR_STATUS = 500

// ─────────────────────────────────────────────────────────────────────────────
// Helpers internos (NÃO são actions Vuex)
// ─────────────────────────────────────────────────────────────────────────────

function resetTurnBuffers(commit) {
  commit('CHAT_RESET_RESPONSE_TOKENS')
  commit('CHAT_RESET_THINKING_STEPS')
  commit('CHAT_RESET_TOOL_CALLS')
}

function clearThread(commit) {
  commit('CHAT_SET_MESSAGES', [])
  commit('CHAT_SET_SELECTED_CONVERSATION', null)
  resetTurnBuffers(commit)
  commit('CHAT_RESET_PENDING_PROPOSALS')
  commit('CHAT_RESET_SYSTEM_MESSAGES')
}

// ─────────────────────────────────────────────────────────────────────────────
// ★ O vocabulário do MOTOR (spec-001 `plataforma-por-chat`, [T018]).
//
// Até esta task o slice tinha um redutor próprio de eventos SSE — um `EVENT_HANDLERS`
// espelhando o `_onEvent*` do IA CRM. Ele sumiu junto com o parser próprio: o
// `APIAgentChat` estende o `APIChat`, e o motor despacha SEMPRE pelos mesmos nomes,
// no namespace da superfície. As actions abaixo são a TRADUÇÃO desses nomes para as
// mutations `CHAT_*` deste slice — nada mais.
//
// ⚠️ Renomear qualquer uma delas quebra o streaming EM SILÊNCIO: o Vuex loga
// "unknown action type" no console e o turno segue sem escrever na tela. Os nomes são
// contrato com `@modules/aawz-intelligence-v2/api/apiChat`.
// ─────────────────────────────────────────────────────────────────────────────

export const setChatLoading = ({ commit }, carregando) => {
  commit('CHAT_SET_STREAMING', Boolean(carregando))
}

export const addMessage = ({ commit }, mensagem) => {
  commit('CHAT_ADD_MESSAGE', mensagem)
}

// O motor manda a conversa inteira (`{id}`); aqui só o id mora no state.
export const setSelectedConversation = ({ commit }, conversa) => {
  commit('CHAT_SET_SELECTED_CONVERSATION', (conversa && conversa.id) || null)
}

// O motor entrega os tokens em LOTE (um flush por quadro, não um por token).
export const addResponseTokens = ({ commit }, lote) => {
  (lote || []).forEach((token) => commit('CHAT_APPEND_RESPONSE_TOKEN', token))
}

export const resetResponseTokens = ({ commit }) => {
  commit('CHAT_RESET_RESPONSE_TOKENS')
}

export const addThinkingStep = ({ commit }, passo) => {
  commit('CHAT_ADD_THINKING_STEP', passo || '')
}

export const resetThinkingSteps = ({ commit }) => {
  commit('CHAT_RESET_THINKING_STEPS')
}

export const addToolCall = ({ commit }, tool) => {
  commit('CHAT_ADD_TOOL_CALL', tool || '')
}

export const removeToolCall = ({ commit }, tool) => {
  commit('CHAT_REMOVE_TOOL_CALL', tool || '')
}

export const resetToolCalls = ({ commit }) => {
  commit('CHAT_RESET_TOOL_CALLS')
}

/**
 * A confirmação pendente do motor vira PROPOSTA aqui.
 *
 * No IA CRM este canal abre um card com botões de confirmar/cancelar. No
 * ia_framework não existe caminho de commit na UI (RF6.1): a escrita não roda no
 * turno e quem decide é a fila do gestor (spec-004). Por isso o mesmo evento cai
 * numa lista informativa — e o getter `pendingConfirmation` deste namespace é
 * fixo em `null`, para o card de confirmação do `messages.vue` reusado nunca
 * aparecer.
 */
export const setPendingConfirmation = ({ commit }, preview) => {
  const p = preview || {}
  commit('CHAT_ADD_PENDING_PROPOSAL', {
    operacao: p.operacao || '',
    entidade: p.entidade || '',
    resumo: p.resumo || '',
  })
}

export const resetPendingConfirmation = ({ commit }) => {
  commit('CHAT_RESET_PENDING_PROPOSALS')
}

// `id_run` do `start`/`end` — correlação com a trilha do run (nunca exibido cru no MVP).
export const setRunIdDoTurno = ({ commit }, idRun) => {
  commit('CHAT_SET_RUN_ID', idRun)
}

/**
 * Os `acoes_pendentes` do `end`: os resumos humanos canônicos (NC-8), que
 * SUBSTITUEM o que os `tool_result` acumularam durante o turno.
 *
 * Guarda só o trio que o cartão mostra — `requer_confirmacao` e afins são do
 * payload da tool, e o slice é a lista do que ficou pendente, não o eco do fio.
 */
export const setAcoesPendentes = ({ commit }, acoes) => {
  commit(
    'CHAT_SET_PENDING_PROPOSALS',
    (acoes || []).map((acao) => ({
      operacao: (acao && acao.operacao) || '',
      entidade: (acao && acao.entidade) || '',
      resumo: (acao && acao.resumo) || '',
    }))
  )
}

/**
 * Falha DENTRO do stream: preserva a resposta parcial marcada como interrompida
 * (wireframe 2.5a) e explica em uma linha. A copy é genérica de propósito — a
 * mensagem do evento `error` do backend já vem sanitizada e não é endereçada ao
 * usuário (`resource_agent_chat.py:43`).
 */
export const registrarErroDoStream = ({ commit, state }, data) => {
  const parcial = (state.chat.responseTokens || []).join('')
  if (parcial.trim()) {
    commit('CHAT_ADD_MESSAGE', {
      id: (data && data.id) || null,
      role: 'assistant',
      content: parcial,
      interrupted: true,
      thinking_steps: state.chat.thinkingSteps || [],
      sources: [],
      tool_calls: [],
      feedback: null,
    })
  }
  resetTurnBuffers(commit)
  commit('CHAT_ADD_SYSTEM_MESSAGE', { status: STREAM_ERROR_STATUS, message: CHAT.STREAM_ERROR })
  commit('CHAT_SET_STREAMING', false)
}

/**
 * Recusa de GATE, antes de o stream abrir (agente pausado, sem permissão, teto
 * diário). ★ TEXTO EXATO do backend — nunca recomposto no front (opacidade do
 * eixo, RF4.1e). O agente pausado/órfão também some do seletor (RF4.2).
 */
export const registrarRecusaDoGate = ({ commit, dispatch }, envelope) => {
  const status = (envelope && envelope.status) || STREAM_ERROR_STATUS
  const message = (envelope && envelope.message) || CHAT.STREAM_ERROR
  resetTurnBuffers(commit)
  commit('CHAT_ADD_SYSTEM_MESSAGE', { status: status, message: message })
  commit('CHAT_SET_STREAMING', false)
  dispatch('fetchAgentsDisponiveis')
}

// ─────────────────────────────────────────────────────────────────────────────
// Actions Vuex
// ─────────────────────────────────────────────────────────────────────────────

export const fetchAgentsDisponiveis = async ({ commit, dispatch }, { preselectId = null } = {}) => {
  commit('CHAT_SET_LOADING_AGENTS', true)

  const r = await apiAgentChat.getAgentsDisponiveis()

  if (r && r.status === STATUS_OK) {
    const agents = r.value || []
    commit('CHAT_SET_AGENTS', agents)
    commit('CHAT_SET_MODULE_FORBIDDEN', false)
    // Pré-seleção via `?agente=` com fallback silencioso para o 1º da lista (F9).
    if (agents.length > 0) {
      const preselect = agents.find((a) => String(a.id) === String(preselectId))
      await dispatch('selectAgent', (preselect || agents[0]).id)
    }
  } else if (r && r.status === STATUS_FORBIDDEN) {
    commit('CHAT_SET_AGENTS', [])
    commit('CHAT_SET_MODULE_FORBIDDEN', true)
  } else {
    commit('CHAT_SET_AGENTS', [])
    dispatch('feedback/showSnack', r, { root: true })
  }

  commit('CHAT_SET_LOADING_AGENTS', false)
}

/**
 * ★ Zera a thread SÓ quando o agente realmente MUDA.
 *
 * `fetchAgentsDisponiveis` termina chamando esta action para re-selecionar o
 * agente corrente — e ele é chamado depois de uma recusa de gate, justamente
 * para o agente pausado/órfão sumir do seletor (RF4.2). Enquanto a limpeza era
 * incondicional, o refresh apagava a thread INTEIRA logo depois de escrever nela
 * o aviso que explica a recusa: a mensagem aparecia e sumia no mesmo turno, e o
 * parceiro ficava com a tela vazia sem nunca saber por que a pergunta não foi
 * respondida. Medido em `chat-junta-sse.unit.js` (o unit antigo não pegava: o
 * dublê devolvia lista de agentes VAZIA, e sem agente não há re-seleção).
 */
export const selectAgent = async ({ commit, dispatch, state }, idInstance) => {
  const trocouDeAgente = state.chat.selectedAgentId !== idInstance
  commit('CHAT_SET_SELECTED_AGENT', idInstance)
  if (trocouDeAgente) clearThread(commit)
  await dispatch('fetchChatConversations')
}

export const fetchChatConversations = async ({ commit, state }) => {
  const idInstance = state.chat.selectedAgentId
  if (!idInstance) {
    commit('CHAT_SET_CONVERSATIONS', [])
    return
  }
  commit('CHAT_SET_LOADING_CONVERSATIONS', true)
  const r = await apiAgentConversations.fetchConversations({ idInstance: idInstance, limit: 30, offset: 0 })
  if (r && r.status === STATUS_OK) {
    commit('CHAT_SET_CONVERSATIONS', r.value || [])
  }
  commit('CHAT_SET_LOADING_CONVERSATIONS', false)
}

export const selectConversation = async ({ commit }, id) => {
  if (!id) return
  commit('CHAT_SET_SELECTED_CONVERSATION', id)
  commit('CHAT_RESET_SYSTEM_MESSAGES')
  commit('CHAT_RESET_PENDING_PROPOSALS')
  commit('CHAT_SET_LOADING_MESSAGES', true)
  commit('CHAT_SET_MESSAGES', [])
  const r = await apiAgentConversations.fetchMessages({ idConversa: id })
  if (r && r.status === STATUS_OK) {
    commit('CHAT_SET_MESSAGES', (r.value && r.value.messages) || [])
  }
  commit('CHAT_SET_LOADING_MESSAGES', false)
}

export const newConversation = ({ commit }) => {
  commit('CHAT_SET_SELECTED_CONVERSATION', null)
  commit('CHAT_SET_MESSAGES', [])
  commit('CHAT_RESET_SYSTEM_MESSAGES')
  commit('CHAT_RESET_PENDING_PROPOSALS')
}

export const renameConversation = async ({ commit }, { id, topic } = {}) => {
  if (!id || !topic) return
  commit('CHAT_UPDATE_CONVERSATION_TOPIC', { id: id, topic: topic })
  await apiAgentConversations.renameConversation({ idConversa: id, topic: topic })
}

export const deleteConversation = async ({ state, dispatch }, id) => {
  if (!id) return
  const r = await apiAgentConversations.deleteConversation({ idConversa: id })
  if (r && r.status === STATUS_OK) {
    if (state.chat.selectedConversationId === id) dispatch('newConversation')
    dispatch('fetchChatConversations')
  }
  dispatch('feedback/showSnack', r, { root: true })
}

/**
 * Abre o turno. Quem empurra a bolha do usuário, zera os buffers e liga o
 * loading é o `resetChatState` do MOTOR (`apiChat.js`) — daí este corpo curto.
 *
 * O que continua sendo desta superfície é a limpeza dos avisos do turno
 * anterior: o gate que recusou a última pergunta não é sobre a próxima.
 */
export const sendMessage = async ({ commit, state }, query) => {
  const text = (query || '').trim()
  if (!text) return
  if (state.chat.streaming) return
  const idInstance = state.chat.selectedAgentId
  if (!idInstance) return

  commit('CHAT_RESET_SYSTEM_MESSAGES')

  await apiAgentChat.sendMessage({
    idInstance: idInstance,
    message: text,
    conversationId: state.chat.selectedConversationId || null,
  })

  // Safety net: reabilita o input mesmo se o stream terminar sem `end`/`error`.
  commit('CHAT_SET_STREAMING', false)
}

export const abortStream = ({ commit, state }) => {
  apiAgentChat.abort()
  const partial = (state.chat.responseTokens || []).join('')
  if (partial.trim()) {
    commit('CHAT_ADD_MESSAGE', {
      id: null,
      role: 'assistant',
      content: partial,
      interrupted: true,
      thinking_steps: state.chat.thinkingSteps || [],
      sources: [],
      tool_calls: [],
      feedback: null,
    })
  }
  resetTurnBuffers(commit)
  commit('CHAT_SET_STREAMING', false)
}
